/**
 * Pagination component for lists and tables
 */

import './Pagination.css';

/**
 * Pagination component
 * @param {Object} props - Component props
 * @param {number} props.currentPage - Current page number
 * @param {number} props.totalItems - Total number of items
 * @param {number|string} props.pageSize - Items per page or 'All'
 * @param {Function} props.onPageChange - Page change handler
 * @param {Function} props.onPageSizeChange - Page size change handler
 * @param {Array} props.pageSizeOptions - Available page size options
 * @returns {React.ReactElement} Pagination element
 */
export default function Pagination({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [20, 50, 100, 'All']
}) {
  const isShowingAll = pageSize === 'All';
  const totalPages = isShowingAll ? 1 : Math.ceil(totalItems / pageSize);

  const getShowingRange = () => {
    if (totalItems === 0) return '0';
    if (isShowingAll) return `1-${totalItems}`;

    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, totalItems);
    return `${start}-${end}`;
  };

  const handlePageSizeChange = (e) => {
    const value = e.target.value === 'All' ? 'All' : Number(e.target.value);
    onPageSizeChange(value);
  };

  return (
    <div className="pagination">
      <div className="pagination__info">
        Showing <strong>{getShowingRange()}</strong> of <strong>{totalItems}</strong>
      </div>

      <div className="pagination__controls">
        <button
          type="button"
          className="pagination__btn"
          disabled={currentPage === 1 || isShowingAll}
          onClick={() => onPageChange(1)}
          aria-label="First page"
        >
          &laquo;
        </button>
        <button
          type="button"
          className="pagination__btn"
          disabled={currentPage === 1 || isShowingAll}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          &lsaquo;
        </button>

        <span className="pagination__current">
          Page {currentPage} of {totalPages}
        </span>

        <button
          type="button"
          className="pagination__btn"
          disabled={currentPage === totalPages || isShowingAll}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
        >
          &rsaquo;
        </button>
        <button
          type="button"
          className="pagination__btn"
          disabled={currentPage === totalPages || isShowingAll}
          onClick={() => onPageChange(totalPages)}
          aria-label="Last page"
        >
          &raquo;
        </button>
      </div>

      <div className="pagination__size">
        <label htmlFor="pageSize" className="pagination__size-label">
          Per page:
        </label>
        <select
          id="pageSize"
          className="pagination__size-select"
          value={pageSize}
          onChange={handlePageSizeChange}
        >
          {pageSizeOptions.map(size => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
